import type { RDMDProps, RMDXishOpts, RMDXOpts } from '@readme/backend/iso';

import loadable from '@loadable/component';

import Dehydrated from './Dehydrated';

/**
 * Dynamically import the HOC for `@readme/markdown` rendering only if needed.
 */
const RMDX = loadable(() => import('./RMDX'), { ssr: true });
const RMDXToc = loadable(() => import('./RMDX'), {
  resolveComponent: components => components.TOC,
  ssr: true,
});

/**
 * Dynamically import the HOC for legacy `@readme/markdown-legacy` rendering only if needed.
 */
const LegacyRDMD = loadable(() => import('./RDMD'), { ssr: true });
const LegacyRDMDToc = loadable(() => import('./RDMD'), {
  resolveComponent: components => components.TOC,
  ssr: true,
});

const RMDXISH = loadable(() => import('./RMDXISH'));
const RMDXISHToc = loadable(() => import('./RMDXISH'), {
  resolveComponent: components => components.TOC,
});

export type Props = RDMDProps & {
  /**
   * Specifies which package to use for rendering the content.
   * - `rdmd` uses the legacy `@readme/markdown-legacy` package.
   * - `rmdx` uses the new `@readme/markdown` package.
   */
  renderer?: 'rdmd' | 'rmdx';
  /**
   * Whether the content is parsed as markdown or mdx when the `@readme/markdown`
   * renderer is used. Defaults to `md` for legacy compatibility.
   * If `mdx` is specified, the rdmx renderer will be automatically selected.
   */
  format?: RMDXOpts['format'];
};

export function useRenderer(props: Props) {
  const { renderer = 'rdmd', format = 'md', opts, ...rest } = props;

  // If the `dehydrated` document from the SSR is provided, use it as a
  // fallback to show content while the renderer's JS modules are being loaded.
  const fallback = props.dehydrated ? <Dehydrated {...props} string={props.dehydrated} Tag={props.Tag} /> : <></>;

  // Determine whether to render the content with rmdx or the legacy rdmd package.
  // If the format is `mdx` we infer that the renderer is `rmdx` because the legacy package
  // does not support MDX content.
  const isRmdx = format === 'mdx' || renderer === 'rmdx';

  // Override the renderer to use the mdxish engine if the project is mdxish enabled
  // For now, it takes precedence over mdx renderer
  const useMdxish = opts && 'mdxish' in opts && opts.mdxish;

  return {
    isRmdx,
    fallback,
    rendererProps: {
      ...rest,
      opts: isRmdx
        ? // Include the `format` option for `@readme/markdown` to determine whether
          // to parse the content as `mdx` or `md`.
          {
            format,
            ...opts,
          }
        : opts,
    },
    useMdxish,
  };
}

/**
 * Strips mdxish-specific keys from RMDXishOpts so it can be passed to RMDX without
 * breaking the type contract. Used when constructing the MDX fallback for migrated projects.
 */
function toMdxOpts(mdxishOpts: RMDXishOpts) {
  const { mdxish: _mdxish, fallbackToMdx: _fallback, componentTypes: _types, ...mdxOpts } = mdxishOpts;
  return { format: 'mdx' as const, ...mdxOpts };
}

/**
 * Renders a Table of Contents for Readme flavored MDX or Markdown content.
 */
export const TOC = (props: Props) => {
  const { isRmdx, fallback, rendererProps, useMdxish } = useRenderer(props);

  if (useMdxish) {
    const mdxishOpts = rendererProps.opts as RMDXishOpts | undefined;
    const tocFallback = mdxishOpts?.fallbackToMdx ? (
      <RMDXToc fallback={fallback} {...rendererProps} opts={toMdxOpts(mdxishOpts)} />
    ) : undefined;
    return <RMDXISHToc {...rendererProps} errorFallback={tocFallback} />;
  }

  return isRmdx ? (
    <RMDXToc fallback={fallback} {...rendererProps} />
  ) : (
    <LegacyRDMDToc fallback={fallback} {...rendererProps} body={props.body || ''} />
  );
};

/**
 * Renders Readme flavored MDX or Markdown content.
 */
export default function RDMD(props: Props) {
  const { isRmdx, fallback, rendererProps, useMdxish } = useRenderer(props);

  if (useMdxish) {
    const mdxishOpts = rendererProps.opts as RMDXishOpts | undefined;
    const mdxFallback = mdxishOpts?.fallbackToMdx ? (
      <RMDX fallback={fallback} {...rendererProps} opts={toMdxOpts(mdxishOpts)} />
    ) : undefined;
    return <RMDXISH {...rendererProps} errorFallback={mdxFallback} />;
  }

  return isRmdx ? (
    <RMDX fallback={fallback} {...rendererProps} />
  ) : (
    <LegacyRDMD fallback={fallback} {...rendererProps} />
  );
}
